import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ROUTES from '../../routes';
import {
  BoxSeparator,
  Button,
  ChangeEmailModal,
  Confirm,
  ContentBox,
  ForbiddenSection,
  Page,
  PlansModal,
  TitleBox,
} from '../../components';
import { user as userActions } from '../../actions';
import { Language } from '../../utils';
import ListItem from '@material-ui/core/ListItem';
import List from '@material-ui/core/List';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import makeClasses from './styles';
import { Grid, Hidden } from '@material-ui/core';
import clsx from 'clsx';
import { Policies } from '../../utils/Policies';
import { Alert, AlertTitle } from '@material-ui/lab';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { push } from 'connected-react-router';
import { checkForExpiredSession } from '../../utils/Utils';

const ProfileScreen = () => {
  const dispatch = useDispatch();
  const classes = makeClasses();

  const [profile, setProfile] = useState({});
  const [hasRUT, setHasRUT] = useState(false);
  const [branches, setBranches] = useState([]);
  const [forbiddenSection, setForbbidenSection] = useState(false);
  const [leftBoxes, setLeftBoxes] = useState([]);
  const [rightBoxes, setRightBoxes] = useState([]);
  const [showPlansModal, setShowPlansModal] = useState({ show: false, branch: null });
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const [sessionExpiredModalTitle, setSessionExpiredModalTitle] = useState('');
  const [sessionExpiredModalMessage, setSessionExpiredModalMessage] = useState('');

  const { policies, authTime, languageTexts } = useSelector((state) => ({
    policies: state.user.userData.policies || [],
    authTime: state.user.userData.authTime || Date.now(),
    languageTexts: state.language.texts || {},
  }));
  const i18n = Language(languageTexts);
  const hasBranches = branches.length > 0;
  const canChangeSensitiveData = policies && policies.includes(Policies.types.SENSITIVE_DATA);

  useEffect(() => {
    window.scrollTo(0, 0);

    if (policies && policies.includes(Policies.types.PROFILE)) {
      _loadProfile();
    } else {
      setForbbidenSection(true);
    }
  }, [policies]);

  const _loadProfile = () => {
    dispatch(
      userActions.getProfileData((profile, branches) => {
        setProfile(profile);
        setHasRUT(profile.merchantIdentifier && profile.merchantIdentifier.merchantDocumentType === 'RUT');

        const _leftBoxes = [];
        const _rightBoxes = [];
        branches.forEach((branch, index) => {
          if (index % 2 === 0) {
            _rightBoxes.push(branch);
          } else {
            _leftBoxes.push(branch);
          }
        });
        setLeftBoxes(_leftBoxes);
        setRightBoxes(_rightBoxes);

        dispatch(
          userActions.getPaymentMethodsByBranches(
            branches.map((b) => b.traceId),
            (paymentMethods) => {
              branches.forEach((branch, index) => {
                branch.paymentMethods = paymentMethods[index];
              });
              setBranches(branches);
            }
          )
        );
      })
    );
  };

  const _togglePlansModal = (branch) => () => {
    const _showPlansModal = !showPlansModal.show;
    setShowPlansModal({
      show: _showPlansModal,
      branch: _showPlansModal ? branch : null,
    });
  };

  const _toggleSessionExpiredModal = (title, message) => {
    setSessionExpiredModalTitle(!showSessionExpiredModal ? title : '');
    setSessionExpiredModalMessage(!showSessionExpiredModal ? message : '');
    setShowSessionExpiredModal(!showSessionExpiredModal);
  };

  const _toggleChangeEmailModal = (success = false) => {
    const exiredSession = checkForExpiredSession(authTime);

    if (exiredSession) {
      return _toggleSessionExpiredModal(
        i18n.get('Profile.ChangeEmailExpiredSessionDialogTitle'),
        i18n.get('Profile.ChangeEmailExpiredSessionDialogContentText')
      );
    }

    setShowChangeEmailModal(!showChangeEmailModal);

    if (success) {
      _loadProfile();
    }
  };

  const _logOut = () => {
    dispatch(userActions.logOut());
  };

  const _goToPaymentMethods = () => {
    dispatch(push(ROUTES.MANAGE_PAYMENT_METHODS.path));
  };

  const renderUserDetails = () => {
    const merchantIdentifier = profile.merchantIdentifier || {};
    return (
      <List className={classes.contentTile}>
        <ListItem className={clsx(classes.contentItem, classes.contentItemColumn, classes.contentItemEven)}>
          <div>{i18n.get('Profile.UserDetailsName')}</div>
          <div className={clsx(classes.contentItemText, classes.contentItemColumnText)}>{profile.businessName}</div>
        </ListItem>
        <ListItem className={clsx(classes.contentItem, classes.contentItemColumn)}>
          <div>{merchantIdentifier.merchantDocumentType}</div>
          <div className={clsx(classes.contentItemText, classes.contentItemColumnText)}>
            {merchantIdentifier.merchantDocument}
          </div>
        </ListItem>
        <ListItem className={clsx(classes.contentItem, classes.contentItemColumn, classes.contentItemEven)}>
          <div>{i18n.get('Profile.UserDetailsEmail')}</div>
          <div className={clsx(classes.contentItemText, classes.contentItemColumnText)}>
            <div>{profile.contactEmail}</div>
            {canChangeSensitiveData ? (
              <Button className={classes.contentItemButton} onClick={_toggleChangeEmailModal}>
                <EditOutlinedIcon className={classes.contentItemButtonIcon} />
              </Button>
            ) : null}
          </div>
        </ListItem>
        {canChangeSensitiveData ? (
          <ListItem className={clsx(classes.contentItem, classes.contentItemColumn)}>
            <div>{i18n.get('Profile.UserDetailsPaymentMethods')}</div>
            <div className={clsx(classes.contentItemText, classes.contentItemColumnText)}>
              <Button className={classes.contentItemButton} onClick={_goToPaymentMethods}>
                <MoreHorizIcon className={classes.contentItemButtonIcon} />
              </Button>
            </div>
          </ListItem>
        ) : null}
        <ListItem className={clsx(classes.contentItem, classes.contentItemColumn, classes.contentItemEven)}>
          <div>{i18n.get('Profile.TermsAndConditions')}</div>
          <div className={clsx(classes.contentItemText, classes.contentItemColumnText)}>
            <a
              className={classes.contentItemLink}
              href={process.env.REACT_APP_TERMS_AND_CONDITIONS}
              target="_blank"
              rel="noreferrer"
            >
              {i18n.get('Profile.TermsAndConditionsLink')}
            </a>
          </div>
        </ListItem>
      </List>
    );
  };

  const renderBoxes = (boxes) => {
    return boxes.map((branch, index) => {
      const hasPaymentMethods = branch.paymentMethods && branch.paymentMethods.length > 0;
      return (
        <React.Fragment key={index}>
          {index !== 0 ? <BoxSeparator /> : null}
          <ContentBox title={i18n.get('Profile.BoxBranchTitle')} titleBold>
            <List className={classes.contentTile}>
              <ListItem className={clsx(classes.contentItem, classes.contentItemColumn, classes.contentItemEven)}>
                <div>{branch.name}</div>
                <div className={clsx(classes.contentItemText, classes.contentItemColumnText)}>{branch.address}</div>
              </ListItem>
              <ListItem className={clsx(classes.contentItem, classes.contentItemColumn)}>
                <div>{i18n.get('Profile.BranchesSectionPlanTitle')}</div>
                <div className={clsx(classes.contentItemText, classes.contentItemColumnText)}>
                  <Button className={classes.contentItemButton} onClick={_togglePlansModal(branch)}>
                    <VisibilityOutlinedIcon className={classes.contentItemButtonIcon} />
                  </Button>
                </div>
              </ListItem>
              <ListItem
                className={clsx(
                  classes.contentItem,
                  classes.contentItemColumn,
                  classes.contentItemEven,
                  classes.contentItemWithSubItems,
                  !hasPaymentMethods ? classes.contentItemWarning : null
                )}
              >
                <div>
                  {hasPaymentMethods
                    ? i18n.get('Profile.BranchesSectionPaymentMethods')
                    : i18n.get('Profile.BranchesSectionPaymentMethodsEmpty')}
                </div>
                <div
                  className={clsx(classes.contentItemText, classes.contentItemColumnText, classes.contentItemSubItem)}
                >
                  {hasPaymentMethods
                    ? branch.paymentMethods.map((paymentMethod, index) => (
                        <div key={index}>
                          {paymentMethod.currencyISO} | {paymentMethod.paymentEntity} | {paymentMethod.accountNumber}
                        </div>
                      ))
                    : null}
                </div>
              </ListItem>
            </List>
          </ContentBox>
        </React.Fragment>
      );
    });
  };

  if (forbiddenSection) {
    return (
      <Page withHeader withSidebar withHeaderTitle={i18n.get('Profile.Title')} withActivePage={ROUTES.PROFILE.id}>
        <ForbiddenSection />
      </Page>
    );
  }

  return (
    <Page withHeader withSidebar withHeaderTitle={i18n.get('Profile.Title')} withActivePage={ROUTES.PROFILE.id}>
      <TitleBox title={i18n.get('Profile.BoxTitle')} />
      <BoxSeparator />
      <Grid container spacing={4}>
        <Grid item xs={12} sm={12} md={6}>
          <ContentBox title={i18n.get('Profile.BoxUserTitle')} titleBold>
            {renderUserDetails()}
          </ContentBox>
          <BoxSeparator />
          <Hidden mdDown>{hasBranches ? renderBoxes(leftBoxes) : null}</Hidden>
          <Hidden mdUp>{hasBranches ? renderBoxes(branches) : null}</Hidden>
        </Grid>
        <Hidden mdDown>
          <Grid item xs={12} sm={12} md={6}>
            {hasBranches ? renderBoxes(rightBoxes) : null}
          </Grid>
        </Hidden>
      </Grid>
      <PlansModal
        open={showPlansModal.show}
        branch={showPlansModal.branch}
        onClose={_togglePlansModal()}
        hasRUT={hasRUT}
      />
      <ChangeEmailModal open={showChangeEmailModal} onClose={_toggleChangeEmailModal} />
      <Confirm
        open={showSessionExpiredModal}
        onClose={_toggleSessionExpiredModal}
        title={sessionExpiredModalTitle}
        onConfirm={_logOut}
        confirmText={i18n.get('Profile.ExpiredSessionDialogActionConfirm')}
        cancelText={i18n.get('Profile.ExpiredSessionDialogActionCancel')}
        onCancel={_toggleSessionExpiredModal}
      >
        <Alert severity="info" icon={false} className={clsx(classes.alert, classes.alertInfo)}>
          <div>
            <InfoOutlinedIcon className={classes.alertIcon} />
          </div>
          <div className={classes.alertMessage}>
            <AlertTitle className={classes.alertTitle}>
              {i18n.get('Profile.ExpiredSessionDialogContentTitle')}
            </AlertTitle>
            {sessionExpiredModalMessage}
          </div>
        </Alert>
      </Confirm>
    </Page>
  );
};

ProfileScreen.id = 'com.Handy.Profile';

export default ProfileScreen;
